import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { AuthShell, FormNotice, PasswordRules } from '@/components/auth-shell';
import { Button, PasswordInput } from '@/components/ui';
import { resetPassword } from '@/lib/account-api';
import { useAuth } from '@/lib/auth-context';
import { meetsPasswordPolicy } from '@/lib/password';
import { errorMessage } from '@/lib/utils';

/** The landing page for the emailed link: `/reset-password?token=…`. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { user, clearSession } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!meetsPasswordPolicy(password)) {
      setError('Your new password does not meet the requirements below.');
      return;
    }

    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);

    try {
      await resetPassword(token, password);
      // The reset revoked every session for the account; if this browser held
      // one, it is dead now — drop it rather than let the next request 401.
      if (user) clearSession();
      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been reset. Sign in with your new password.' },
      });
    } catch (cause) {
      setError(errorMessage(cause, 'Could not reset your password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell subtitle="Reset your password">
        <div className="space-y-4">
          <FormNotice tone="danger">
            This reset link is incomplete. Open the link from your email again, or request a new one.
          </FormNotice>
          <Link
            to="/forgot-password"
            className="block text-center text-sm font-medium text-brand-600 hover:underline"
          >
            Request a new link
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell subtitle="Choose a new password">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <FormNotice tone="danger">
            {error}{' '}
            {/* An expired link is the common failure; offer the way out. */}
            {/expired|invalid/i.test(error) && (
              <Link to="/forgot-password" className="font-medium underline">
                Request a new link
              </Link>
            )}
          </FormNotice>
        )}

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-ink">
            New password
          </label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="new-password"
            autoFocus
            aria-describedby="password-rules"
          />
          <PasswordRules id="password-rules" value={password} />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-ink">
            Confirm new password
          </label>
          <PasswordInput
            id="confirmPassword"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            required
            autoComplete="new-password"
            invalid={confirm.length > 0 && confirm !== password}
          />
        </div>

        <Button type="submit" fullWidth disabled={submitting}>
          {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          {submitting ? 'Saving…' : 'Reset password'}
        </Button>
      </form>

      <Link
        to="/login"
        className="mt-5 flex items-center justify-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back to sign in
      </Link>
    </AuthShell>
  );
}
